import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Table, useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { screenerClient } from '../../api'
import { usePaymentGate } from '../../payment/usePaymentGate'
import type { Coin } from '../../api/types'

const DEFAULT_COIN2_FOR_VIRTUAL = 'BTC'
const DEFAULT_COIN2_FOR_VIRTUAL_SECONDARY = 'ETH'

type ListType = 'normal' | 'reverse' | 'virtual'

interface Row {
  coin: Coin
  href: string
}

/** Equivalent of legacy `CoinListUniversalComponent` (audit §3/§6 — the
 * "main screener page"): fetches `GET /coins/closed`, hard-filters to
 * `coin2 === 'USDT'` (legacy behaviour kept exactly, `coin-list-universal.
 * component.ts:87`), and is gated behind the subscription paywall. Sorting
 * is now the shared `Table`'s (fleet frontend-standards §1) instead of the
 * legacy component's manual client-side sort — same default order
 * (alphabetical by `coin1`), now also user-re-sortable. */
export function CoinsPage() {
  const { type: rawType } = useParams<{ type?: string }>()
  const type: ListType = rawType === 'reverse' || rawType === 'virtual' ? rawType : 'normal'
  const { paymentRequired, loading: paymentLoading } = usePaymentGate()
  const [search, setSearch] = useState('')

  const { data: coins, loading, error } = useApiQuery(
    () => (paymentRequired === 0 ? screenerClient.searchClosedCoins() : Promise.resolve([])),
    [paymentRequired],
  )

  useEffect(() => {
    void screenerClient.logEvent('page', 'view')
  }, [])

  const rows = useMemo<Row[]>(() => {
    if (!coins) return []
    const usdtOnly = coins.filter((c) => c.coin2 === 'USDT')
    const term = search.trim().toUpperCase()
    const filtered = term ? usdtOnly.filter((c) => c.coin1.toUpperCase().includes(term)) : usdtOnly
    return filtered
      .map((coin): Row | null => {
        const exchangeId = coin.exchangeId ?? 'binance'
        const tradingType = 'spot'
        if (type === 'normal') {
          return { coin, href: `/${exchangeId}/${tradingType}/coins/${coin.coin1}/${coin.coin2}/closedprice` }
        }
        if (type === 'reverse') {
          return { coin, href: `/${exchangeId}/${tradingType}/coins/${coin.coin2}/${coin.coin1}/closedprice` }
        }
        // virtual
        if (coin.coin1 === DEFAULT_COIN2_FOR_VIRTUAL) {
          return {
            coin,
            href: `/${exchangeId}/${tradingType}/coins/${coin.coin1}/${DEFAULT_COIN2_FOR_VIRTUAL_SECONDARY}/closedprice`,
          }
        }
        return { coin, href: `/${exchangeId}/${tradingType}/coins/${coin.coin1}/${DEFAULT_COIN2_FOR_VIRTUAL}/closedprice` }
      })
      .filter((r): r is Row => r !== null)
  }, [coins, search, type])

  const columns: Column<Row>[] = [
    {
      key: 'coin1',
      label: 'Coin',
      align: 'left',
      sortValue: (row) => row.coin.coin1,
      format: (_v, row) => <Link to={row.href}>{row.coin.coin1}</Link>,
    },
  ]

  return (
    <div>
      <div className="screener-page-title">
        <h1>Coins</h1>
        <nav className="screener-nav">
          <Link to="/coins/normal" aria-current={type === 'normal' ? 'page' : undefined}>
            Normal
          </Link>
          <Link to="/coins/reverse" aria-current={type === 'reverse' ? 'page' : undefined}>
            Reverse
          </Link>
          <Link to="/coins/virtual" aria-current={type === 'virtual' ? 'page' : undefined}>
            Virtual
          </Link>
        </nav>
      </div>

      {paymentRequired === 1 && (
        <div className="screener-card" role="alert">
          <strong>Your system subscription hasn&apos;t been started yet, or expired!</strong>
          <br />
          Please check it on the <Link to="/payment">payment page</Link>.
        </div>
      )}

      {paymentRequired === 0 && (
        <>
          <div className="screener-toolbar">
            <input
              type="search"
              placeholder="search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search coins"
            />
          </div>
          {(loading || paymentLoading) && <p>Loading…</p>}
          {error && <p role="alert">Could not load coins.</p>}
          {!loading && !error && (
            <Table columns={columns} rows={rows} rowKey={(row) => `${row.coin.coin1}-${row.coin.coin2}`} />
          )}
        </>
      )}
    </div>
  )
}
